class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/**
 * 折叠手风琴，适配动画效果，包裹于 <animation-details>
 */
class AnimationAnimationDetails extends HTMLElement {
  constructor() {
    super();
    const detailsList = this.querySelectorAll("animation-details");
    this.addEventListener("details:open", (e) => {
      const target = e.target;
      for (const details of detailsList) {
        if (target != details) {
          details.closeDetails();
        }
      }
    });
    // this.addEventListener("details:close", (e) => {});
  }
}
customElements.define('accordion-animation-details', AnimationAnimationDetails);

/**
 * 适配动画效果，包裹于原生 <details>
 * 类名 details-opening 标记 details 正在开启的过渡动画中
 * 类名 details-opening 标记 details 正在关闭的过渡动画中
 */
class AnimationDetails extends HTMLElement {
  maxElapsedTime = 200;

  constructor() {
    super();
    this.useDocumentResizeObserver = this.getAttribute('data-use-document-resize-observer') == 'true';
  }

  init() {
    this.mainDetailsToggle = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.summary.nextElementSibling;

    // Recheck the open state when the component is connected
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.openDetails();
    } else {
      this.closeDetails();
    }

    this._onSummaryClick = this.onSummaryClick.bind(this);
    this.summary.addEventListener('click', this._onSummaryClick);

    this.resizeObserver = new ResizeObserver(this.resize.bind(this));
    this.resizeObserver.observe(this.useDocumentResizeObserver ? document.body : this.content);
  }

  connectedCallback() {
    this.init();

    // Observe changes to the element's children
    this.observer = new MutationObserver(() => {
      this.summary.removeEventListener('click', this._onSummaryClick);
      this.init();
    });
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.summary.removeEventListener('click', this._onSummaryClick);
  }

  onSummaryClick(event) {
    const isOpen = this.mainDetailsToggle.hasAttribute('open');
    const isOpening = this.mainDetailsToggle.classList.contains('details-opening');
    const isClosing = this.mainDetailsToggle.classList.contains('details-closing');
    if (isOpening || isClosing) {
      event.preventDefault();
      return false;
    } else if (isOpen) {
      this.closeDetails();
      event.preventDefault();
    } else {
      this.openDetails();
    }
  }

  get isOpened() {
    return this.mainDetailsToggle.hasAttribute('open');
  }

  openDetails() {
    this.mainDetailsToggle.classList.add('details-opening');
    setTimeout(() => {
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.style.height = this.content.clientHeight + this.summary.clientHeight + 'px';
      this.summary.setAttribute('aria-expanded', true);
      this.dispatchEvent(new CustomEvent("details:open", { bubbles: true }));
    });
    setTimeout(() => {
      this.mainDetailsToggle.classList.remove('details-opening');
    }, this.maxElapsedTime);
  }

  closeDetails() {
    this.mainDetailsToggle.classList.remove('details-opening');
    this.mainDetailsToggle.classList.add('details-closing');
    this.mainDetailsToggle.style.height = this.summary.clientHeight + 'px';
    this.closeAnimation(this.mainDetailsToggle);
    this.dispatchEvent(new CustomEvent("details:close", { bubbles: true }));
  }

  toggleDetails() {
    if (this.isOpened) {
      this.closeDetails();
    } else {
      this.openDetails();
    }
  }

  resize(entries, observer) {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.style.height = this.content.clientHeight + this.summary.clientHeight + 'px';
    } else {
      this.mainDetailsToggle.style.height = '';
    }
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < this.maxElapsedTime) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.mainDetailsToggle.removeAttribute('open');
        this.mainDetailsToggle.classList.remove('details-opening');
        this.mainDetailsToggle.classList.remove('details-closing');
        if (this.mainDetailsToggle.closest('details[open]')) {
          trapFocus(this.mainDetailsToggle.closest('details[open]'), this.mainDetailsToggle.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('animation-details', AnimationDetails);
